import { HubConnectionBuilder, HubConnectionState, JsonHubProtocol, LogLevel } from "@microsoft/signalr";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appStore } from "index";
import { getConfig } from "plugins/config-loader";
import { datapointsKey } from "store/middleware/analytics/analytics-grouping-middleware";
import { setConnectionId, setEntity, updateQueriesAsFinished } from "store/signalR";
const handleComparisonResponse = (update, dispatch) => {
    update.responses?.forEach((response) => {
        const idSplited = response.id?.split(".");
        const id = idSplited?.[idSplited.length - 1];
        if (id) {
            dispatch(setEntity({ id, requestId: update.id ?? "", response }));
        }
    });
    if (update.id) {
        dispatch(updateQueriesAsFinished(update.id));
    }
};
const handleStatusChange = (update, dispatch) => {
    if (update) {
        handleComparisonResponse(update, dispatch);
    }
};
const startSignalRConnection = async (connection, dispatch, requests, postStartConfigure) => {
    const timeOut = 5000;
    try {
        await connection.start();
        if (connection.connectionId) {
            dispatch(setConnectionId({ id: connection.connectionId, requests }));
            if (postStartConfigure) {
                postStartConfigure(connection);
            }
        }
    }
    catch {
        setTimeout(() => startSignalRConnection(connection, dispatch, requests, postStartConfigure), timeOut);
    }
};
const getAppUrl = () => {
    const config = getConfig("REACT_APP_BACKEND_BASE_URL");
    if (config?.indexOf("http") === 0) {
        return config;
    }
    const url = new URL(config, window.location.origin);
    return url.toString();
};
const prepareConnection = (hub) => {
    const options = {
        accessTokenFactory: () => appStore?.getState()?.auth?.oidc?.user?.access_token,
        withCredentials: false
    };
    const url = new URL(hub, getAppUrl()).toString();
    const connection = new HubConnectionBuilder()
        .withUrl(url, options)
        .withAutomaticReconnect()
        .withHubProtocol(new JsonHubProtocol())
        .configureLogging(LogLevel.None)
        .build();
    return connection;
};
// We cannot add connections themselves to the store because they are not serializable, so we set them as "global" variables
const connections = new Map();
export const useSignalRHub = (hub, configure) => {
    const token = useSelector((state) => state.auth.oidc?.user?.access_token);
    useEffect(() => {
        if (token && !connections.has(hub)) {
            const connection = prepareConnection(hub);
            configure(connection);
            connections.set(hub, connection);
            window.addEventListener("beforeunload", () => connection.stop());
        }
    }, [configure, hub, token]);
    return connections.get(hub);
};
export const useDatapointsSignalRHub = () => {
    const dispatch = useDispatch();
    const connection = useSignalRHub("datapoints-hub", useCallback((conn) => {
        conn.on("datapoints", (update) => handleStatusChange(update, dispatch));
        startSignalRConnection(conn, dispatch, [datapointsKey]);
    }, [dispatch]));
    return connection;
};
export const useExportDataSignalRHub = (callbacks) => {
    const connection = useSignalRHub("raw-template-hub", useCallback((_connection) => {
        if (_connection && _connection.state !== HubConnectionState.Connected) {
            _connection.on("ProcessCompleted", (operationId, completedSuccessfully, documentId, errorMessage) => {
                callbacks?.onProcessCompleted?.(operationId, completedSuccessfully, documentId, errorMessage);
            });
            _connection.on("ProcessStarted", (operationId) => {
                callbacks?.onProcessStarted?.(operationId);
            });
            _connection.on("ProcessStepCompleted", (operationId, percentage) => {
                callbacks?.onProcessStepCompleted?.(operationId, percentage);
            });
            _connection.start()
                .then()
                .catch();
        }
    }, [callbacks]));
    return connection;
};
export const getConnectionId = (hubName) => connections.get(hubName)?.connectionId ?? "";
